import { useEffect, useState, useCallback } from 'react';
import { urgentTasksAPI } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { AlertTriangle, Clock, CheckCircle2, Loader2 } from 'lucide-react';

function fmtDeadline(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function ActionRequiredPage() {
  const [active, setActive] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [act, all] = await Promise.all([
        urgentTasksAPI.active().then((r) => r.data?.items || []).catch(() => []),
        urgentTasksAPI.list({ status: 'completed' }).then((r) => r.data?.items || []).catch(() => []),
      ]);
      setActive(act);
      setCompleted(all);
    } catch {
      toast.error('Could not load your action items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markDone = async (task) => {
    setBusy(task.task_id);
    try {
      await urgentTasksAPI.complete(task.task_id);
      setActive((prev) => prev.filter((t) => t.task_id !== task.task_id));
      toast.success('Marked as completed');
      load();
    } catch {
      toast.error('Could not update task');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="action-required-page">
      <PageHeader
        title="Action Required"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Action Required', href: '/action-required' },
        ]}
      />
      <div className="page-content max-w-3xl space-y-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-red-600" size={18} />
                <h2 className="text-lg font-semibold text-slate-900">
                  Pending items ({active.length})
                </h2>
              </div>
              {active.length === 0 ? (
                <div
                  data-testid="action-required-empty"
                  className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500"
                >
                  You're all caught up. No urgent tasks require your action.
                </div>
              ) : (
                <div className="space-y-2">
                  {active.map((t) => (
                    <div
                      key={t.task_id}
                      data-testid={`action-task-${t.task_id}`}
                      className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${
                        t.is_overdue ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                            t.is_overdue ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
                          }`}>
                            {t.is_overdue ? 'Overdue' : t.priority}
                          </span>
                          <span className="text-xs text-slate-500">{t.task_type}</span>
                        </div>
                        <p className="font-semibold text-slate-900 mt-1">{t.title}</p>
                        {t.description ? (
                          <p className="text-sm text-slate-600 mt-0.5">{t.description}</p>
                        ) : null}
                        <p className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                          <Clock size={12} /> Due {fmtDeadline(t.deadline)}
                        </p>
                        {t.link ? (
                          <a
                            href={t.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`action-task-link-${t.task_id}`}
                            className="text-xs text-sky-700 hover:underline font-medium mt-1 inline-block"
                          >
                            Open link →
                          </a>
                        ) : null}
                      </div>
                      <button
                        data-testid={`action-task-done-${t.task_id}`}
                        onClick={() => markDone(t)}
                        disabled={busy === t.task_id}
                        className="shrink-0 inline-flex items-center gap-1 rounded-md bg-[#0f2547] text-white text-xs font-semibold px-3 py-2 hover:bg-[#13315f] disabled:opacity-60"
                      >
                        {busy === t.task_id
                          ? <Loader2 size={14} className="animate-spin" />
                          : <CheckCircle2 size={14} />} Completed
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {completed.length > 0 && (
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                  Recently completed
                </h2>
                <div className="space-y-2">
                  {completed.slice(0, 20).map((t) => (
                    <div
                      key={t.task_id}
                      data-testid={`action-task-completed-${t.task_id}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 opacity-80"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-green-600 text-white">
                            Done
                          </span>
                          <span className="text-xs text-slate-500">{t.task_type}</span>
                        </div>
                        <p className="font-medium text-slate-700 truncate">{t.title}</p>
                      </div>
                      <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
